/** Profile settings — Search region (Settings page) helpers. */

import type { Region } from '@/types/regions'

export const FAIR_PROFILE_V2_CITY_STORAGE_KEY = 'selectedCity'

/* ── Settings page (Figma `Settings · desktop` 3047:70313) ─────────────── */

/** What `selectedCity` holds. `cityId` is null for a country-only region (BE-146). */
export interface FairProfileV2StoredRegion {
  cityId: string | null
  cityName?: string
  countryName: string
  countryCode?: string
}

export interface FairProfileV2RegionChoice {
  countryId: string | null
  cityId: string | null
}

export interface FairProfileV2RegionOption {
  id: string
  label: string
}

/** Search region row value: `Belarus · Minsk`, `Belarus`, or null when not set. */
export function formatFairProfileV2Region(
  countryName: string | null | undefined,
  cityName?: string | null,
): string | null {
  if (!countryName) {
    return null
  }
  return cityName ? `${countryName} · ${cityName}` : countryName
}

export function fairProfileV2CountryOptions(
  regions: Region[],
): FairProfileV2RegionOption[] {
  return regions.map((region) => ({
    id: region.country.id,
    label: region.country.name,
  }))
}

export function fairProfileV2CityOptions(
  regions: Region[],
  countryId: string | null,
): FairProfileV2RegionOption[] {
  const region = regions.find((candidate) => candidate.country.id === countryId)
  return (region?.cities ?? []).map((city) => ({ id: city.id, label: city.name }))
}

export function filterFairProfileV2RegionOptions(
  options: FairProfileV2RegionOption[],
  query: string,
): FairProfileV2RegionOption[] {
  const needle = query.trim().toLowerCase()
  if (!needle) {
    return options
  }
  return options.filter((option) => option.label.toLowerCase().includes(needle))
}

/**
 * The API selection wins, except over a country-only region: the API cannot
 * hold one yet (BE-146), so the stored choice is the newer of the two. Stored
 * names are matched back to ids.
 */
export function resolveFairProfileV2RegionChoice(
  regions: Region[],
  current: { cityId: string; countryId: string } | null | undefined,
  stored: FairProfileV2StoredRegion | null,
): FairProfileV2RegionChoice {
  if (current && stored?.cityId !== null) {
    return { countryId: current.countryId, cityId: current.cityId }
  }
  if (!stored) {
    return { countryId: null, cityId: null }
  }
  const region = regions.find(
    (candidate) => candidate.country.name === stored.countryName,
  )
  if (!region) {
    return { countryId: null, cityId: null }
  }
  const city = stored.cityId
    ? region.cities.find((candidate) => candidate.id === stored.cityId)
    : undefined
  return { countryId: region.country.id, cityId: city?.id ?? null }
}

export function describeFairProfileV2RegionChoice(
  regions: Region[],
  choice: FairProfileV2RegionChoice,
): { countryName: string; cityName: string | null } | null {
  const region = regions.find(
    (candidate) => candidate.country.id === choice.countryId,
  )
  if (!region) {
    return null
  }
  const city = region.cities.find((candidate) => candidate.id === choice.cityId)
  return { countryName: region.country.name, cityName: city?.name ?? null }
}

/** Google Calendar row (`03 Google Calendar row`, 3048:17662). */
export type FairProfileV2CalendarState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnecting'
  | 'error'
  | 'declined'

/** i18n key under `profile.settings.calendar.*` for the row subtitle. */
export const FAIR_PROFILE_V2_CALENDAR_HINT_KEYS: Record<
  FairProfileV2CalendarState,
  string
> = {
  idle: 'hint',
  connecting: 'connecting',
  connected: 'connected',
  disconnecting: 'connected',
  error: 'error',
  declined: 'declined',
}

/** Maps `?calendar=` on return from Google to a row state (null = no result). */
export function parseFairProfileV2CalendarResult(
  value: unknown,
): FairProfileV2CalendarState | null {
  if (value === 'connected') return 'connected'
  if (value === 'declined' || value === 'access_denied') return 'declined'
  if (value === 'error') return 'error'
  return null
}

export function readFairProfileV2StoredRegion(): FairProfileV2StoredRegion | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const stored = window.localStorage.getItem(FAIR_PROFILE_V2_CITY_STORAGE_KEY)
    return stored ? (JSON.parse(stored) as FairProfileV2StoredRegion) : null
  } catch {
    return null
  }
}

export function writeFairProfileV2StoredRegion(region: FairProfileV2StoredRegion): void {
  if (typeof window === 'undefined') {
    return
  }
  window.localStorage.setItem(
    FAIR_PROFILE_V2_CITY_STORAGE_KEY,
    JSON.stringify(region),
  )
}
